import { type AccountSave, legacyRank } from '../../game/account';
import { type CharacterSave, itemLevel } from '../../game/character';
import { GEAR_SLOTS } from '../../game/items';
import { BOND_STAGES, type Progression } from '../../game/Progression';
import { addTitle } from './titles';

/**
 * Achievements (research 02 §9.6): roster-wide milestones evaluated from
 * character counters and account data. Unlocks are stored in
 * `account.achievements[id] = timestamp`; rewards are silver/gold/items,
 * Legacy XP and titles.
 */

export type AchievementCategory = 'Combat' | 'Progression' | 'Collection' | 'Adventure';

export interface AchievementReward {
  silver?: number;
  gold?: number;
  items?: { id: string; count: number }[];
  title?: string;
}

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;
  cat: AchievementCategory;
  target: number;
  progress: (a: AccountSave, c: CharacterSave) => number;
  reward: AchievementReward;
}

/** Legacy XP granted with every achievement (roster experience). */
export const ACHIEVEMENT_LEGACY_XP = 300;

const n = (c: CharacterSave, key: string) => c.counters[key] ?? 0;
const kills = (c: CharacterSave, ids: string[]) =>
  ids.filter((id) => n(c, `kill_${id}`) > 0).length;
const COLOSSI = ['cinderhide', 'gloomfang', 'rimebound', 'dunestrider'];

function maxPlus(c: CharacterSave): number {
  let best = 0;
  for (const s of GEAR_SLOTS) best = Math.max(best, c.equipment[s]?.plus ?? 0);
  for (const it of c.inventory.items) if (it.kind === 'gear') best = Math.max(best, it.plus);
  return best;
}

function bestBondStage(c: CharacterSave): number {
  let best = 0;
  for (const pts of Object.values(c.bonds)) {
    BOND_STAGES.forEach((t, i) => {
      if (pts >= t) best = Math.max(best, i);
    });
  }
  return best;
}

const bestLifeSkill = (c: CharacterSave) =>
  Math.max(1, ...Object.values(c.lifeSkills).map((l) => l.level));

const def = (d: AchievementDef): AchievementDef => d;

export const ACHIEVEMENTS: AchievementDef[] = [
  // Combat
  def({
    id: 'first_blood',
    name: 'First Blood',
    desc: 'Defeat a monster.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => n(c, 'kills'),
    reward: { silver: 100 },
  }),
  def({
    id: 'slayer_250',
    name: 'Monster Hunter',
    desc: 'Defeat 250 monsters.',
    cat: 'Combat',
    target: 250,
    progress: (_a, c) => n(c, 'kills'),
    reward: { silver: 1500 },
  }),
  def({
    id: 'slayer_1000',
    name: 'Scourge of the Wilds',
    desc: 'Defeat 1,000 monsters.',
    cat: 'Combat',
    target: 1000,
    progress: (_a, c) => n(c, 'kills'),
    reward: { title: 'Scourge of the Wilds' },
  }),
  def({
    id: 'slayer_5000',
    name: 'Harbinger',
    desc: 'Defeat 5,000 monsters.',
    cat: 'Combat',
    target: 5000,
    progress: (_a, c) => n(c, 'kills'),
    reward: { gold: 50, title: 'Harbinger' },
  }),
  def({
    id: 'field_boss_1',
    name: 'Big Game',
    desc: 'Defeat a field boss.',
    cat: 'Combat',
    target: 1,
    progress: (a) => a.collectibles.fieldBosses.length,
    reward: { silver: 1000 },
  }),
  def({
    id: 'field_boss_3',
    name: 'Trophy Wall',
    desc: 'Defeat three different field bosses.',
    cat: 'Combat',
    target: 3,
    progress: (a) => a.collectibles.fieldBosses.length,
    reward: { title: 'Bosshunter' },
  }),
  def({
    id: 'crypt',
    name: 'Into the Warren',
    desc: 'Defeat the Grubling Chieftain in the Hollowroot Crypt.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => kills(c, ['grubling_chieftain']),
    reward: { silver: 1500 },
  }),
  def({
    id: 'tomb',
    name: 'Sand in the Gears',
    desc: 'Defeat the Sandbound Warden in the Sandsworn Tomb.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => kills(c, ['sandbound_warden']),
    reward: { silver: 3000 },
  }),
  def({
    id: 'keep',
    name: 'The Thaw',
    desc: 'Defeat the Frost Matriarch in Frostfang Keep.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => kills(c, ['frost_matriarch']),
    reward: { title: 'Frostbreaker' },
  }),
  def({
    id: 'colossus_1',
    name: 'Colossus Slayer',
    desc: 'Defeat a colossus.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => kills(c, COLOSSI),
    reward: { items: [{ id: 'leap_crystal', count: 10 }] },
  }),
  def({
    id: 'colossus_4',
    name: 'Titan’s Bane',
    desc: 'Defeat all four colossi.',
    cat: 'Combat',
    target: 4,
    progress: (_a, c) => kills(c, COLOSSI),
    reward: { title: 'Titan’s Bane' },
  }),
  def({
    id: 'deepvault',
    name: 'Reliquary Raider',
    desc: 'Defeat the Tide Siren in the Sunken Reliquary.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => kills(c, ['tide_siren']),
    reward: { title: 'Reliquary Raider' },
  }),
  def({
    id: 'siege',
    name: 'Throne Breaker',
    desc: 'Defeat the phantom of Vhal’Zeroth on the Hollow Throne.',
    cat: 'Combat',
    target: 1,
    progress: (_a, c) => kills(c, ['vhalzeroth_phantom']),
    reward: { gold: 500, title: 'Lanternbearer' },
  }),
  // Progression
  def({
    id: 'level_10',
    name: 'Finding Your Feet',
    desc: 'Reach level 10.',
    cat: 'Progression',
    target: 10,
    progress: (_a, c) => c.level,
    reward: { silver: 1000 },
  }),
  def({
    id: 'level_20',
    name: 'Seasoned',
    desc: 'Reach level 20.',
    cat: 'Progression',
    target: 20,
    progress: (_a, c) => c.level,
    reward: { silver: 3000 },
  }),
  def({
    id: 'level_30',
    name: 'Shardbearer',
    desc: 'Reach level 30.',
    cat: 'Progression',
    target: 30,
    progress: (_a, c) => c.level,
    reward: { gold: 100, title: 'Shardbearer' },
  }),
  def({
    id: 'ilvl_300',
    name: 'Tempered Steel',
    desc: 'Reach item level 300.',
    cat: 'Progression',
    target: 300,
    progress: (_a, c) => Math.floor(itemLevel(c)),
    reward: { items: [{ id: 'catalyst', count: 5 }] },
  }),
  def({
    id: 'ilvl_340',
    name: 'Hollowbane',
    desc: 'Reach item level 340.',
    cat: 'Progression',
    target: 340,
    progress: (_a, c) => Math.floor(itemLevel(c)),
    reward: { title: 'Hollowbane' },
  }),
  def({
    id: 'temper_1',
    name: 'Into the Fire',
    desc: 'Attempt a tempering.',
    cat: 'Progression',
    target: 1,
    progress: (_a, c) => n(c, 'temperAttempts'),
    reward: { silver: 500 },
  }),
  def({
    id: 'temper_50',
    name: 'Forge-Touched',
    desc: 'Attempt 50 temperings.',
    cat: 'Progression',
    target: 50,
    progress: (_a, c) => n(c, 'temperAttempts'),
    reward: { title: 'Forge-Touched' },
  }),
  def({
    id: 'temper_15',
    name: 'Masterwork',
    desc: 'Temper a piece of gear to +15.',
    cat: 'Progression',
    target: 15,
    progress: (_a, c) => maxPlus(c),
    reward: { title: 'Master of the Forge' },
  }),
  def({
    id: 'legacy_5',
    name: 'A Legacy Begins',
    desc: 'Reach Legacy Rank 5.',
    cat: 'Progression',
    target: 5,
    progress: (a) => legacyRank(a.legacyXp),
    reward: { silver: 2000 },
  }),
  def({
    id: 'legacy_15',
    name: 'A Name Remembered',
    desc: 'Reach Legacy Rank 15.',
    cat: 'Progression',
    target: 15,
    progress: (a) => legacyRank(a.legacyXp),
    reward: { title: 'Veteran of the Isles' },
  }),
  // Collection
  def({
    id: 'seeds_10',
    name: 'Seedling',
    desc: 'Collect 10 Glimmerseeds.',
    cat: 'Collection',
    target: 10,
    progress: (a) => a.collectibles.seeds.length,
    reward: { silver: 1000 },
  }),
  def({
    id: 'seeds_30',
    name: 'Glimmer-Seeker',
    desc: 'Collect 30 Glimmerseeds.',
    cat: 'Collection',
    target: 30,
    progress: (a) => a.collectibles.seeds.length,
    reward: { title: 'Glimmer-Seeker' },
  }),
  def({
    id: 'hearts_3',
    name: 'Heartfelt',
    desc: 'Claim three Hearts.',
    cat: 'Collection',
    target: 3,
    progress: (a) => a.collectibles.hearts.length,
    reward: { items: [{ id: 'skill_potion', count: 1 }] },
  }),
  def({
    id: 'vistas_5',
    name: 'Wanderer',
    desc: 'Discover five vistas.',
    cat: 'Collection',
    target: 5,
    progress: (a) => a.collectibles.vistas.length,
    reward: { title: 'Wanderer' },
  }),
  def({
    id: 'lore_5',
    name: 'Loremaster',
    desc: 'Record five lore entries.',
    cat: 'Collection',
    target: 5,
    progress: (a) => a.collectibles.lore.length,
    reward: { silver: 1500 },
  }),
  def({
    id: 'cards_10',
    name: 'Card Sharp',
    desc: 'Own ten relic cards.',
    cat: 'Collection',
    target: 10,
    progress: (a) => Object.keys(a.cards.owned).length,
    reward: { silver: 2500 },
  }),
  // Adventure
  def({
    id: 'bond_2',
    name: 'Friendly Face',
    desc: 'Reach Friendly with anyone.',
    cat: 'Adventure',
    target: 2,
    progress: (_a, c) => bestBondStage(c),
    reward: { silver: 1000 },
  }),
  def({
    id: 'bond_4',
    name: 'Devoted',
    desc: 'Reach Devoted with anyone.',
    cat: 'Adventure',
    target: 4,
    progress: (_a, c) => bestBondStage(c),
    reward: { title: 'Beloved of the Isles' },
  }),
  def({
    id: 'bounty_10',
    name: 'On the Board',
    desc: 'Claim 10 bounties.',
    cat: 'Adventure',
    target: 10,
    progress: (_a, c) => n(c, 'bountiesDaily') + n(c, 'bountiesWeekly'),
    reward: { items: [{ id: 'bounty_seal', count: 5 }] },
  }),
  def({
    id: 'bounty_50',
    name: 'Bounty Hunter',
    desc: 'Claim 50 bounties.',
    cat: 'Adventure',
    target: 50,
    progress: (_a, c) => n(c, 'bountiesDaily') + n(c, 'bountiesWeekly'),
    reward: { title: 'Bounty Hunter' },
  }),
  def({
    id: 'rest_7',
    name: 'A Week Well Spent',
    desc: 'Rest seven times.',
    cat: 'Adventure',
    target: 7,
    progress: (_a, c) => n(c, 'restDays'),
    reward: { silver: 2000 },
  }),
  def({
    id: 'waystones_5',
    name: 'Waymaker',
    desc: 'Attune five Waystones.',
    cat: 'Adventure',
    target: 5,
    progress: (_a, c) => c.waystones.length,
    reward: { silver: 1500 },
  }),
  def({
    id: 'market_1',
    name: 'Open for Business',
    desc: 'Complete a trade on the market.',
    cat: 'Adventure',
    target: 1,
    progress: (_a, c) => n(c, 'marketSales') + n(c, 'marketBuys'),
    reward: { gold: 5 },
  }),
  def({
    id: 'wealth',
    name: 'Silver Tongue',
    desc: 'Hold 100,000 silver.',
    cat: 'Adventure',
    target: 100_000,
    progress: (a) => a.wallet.silver,
    reward: { title: 'Silver Tongue' },
  }),
  def({
    id: 'life_5',
    name: 'Journeyman',
    desc: 'Reach level 5 in a life skill.',
    cat: 'Adventure',
    target: 5,
    progress: (_a, c) => bestLifeSkill(c),
    reward: { silver: 1500 },
  }),
  def({
    id: 'life_10',
    name: 'Artisan',
    desc: 'Reach level 10 in a life skill.',
    cat: 'Adventure',
    target: 10,
    progress: (_a, c) => bestLifeSkill(c),
    reward: { title: 'Artisan' },
  }),
];

export const ACHIEVEMENT_BY_ID: Record<string, AchievementDef> = Object.fromEntries(
  ACHIEVEMENTS.map((x) => [x.id, x]),
);

export interface AchievementState {
  def: AchievementDef;
  progress: number;
  done: boolean;
  unlockedAt: number | null;
}

export function achievementStates(a: AccountSave, c: CharacterSave): AchievementState[] {
  return ACHIEVEMENTS.map((d) => {
    const at = a.achievements[d.id] ?? null;
    const progress = Math.min(d.target, Math.max(0, d.progress(a, c)));
    return { def: d, progress: at ? d.target : progress, done: !!at, unlockedAt: at };
  });
}

/** Achievements whose goal is met but that are not recorded yet. */
export function pendingUnlocks(a: AccountSave, c: CharacterSave): AchievementDef[] {
  return ACHIEVEMENTS.filter((d) => !a.achievements[d.id] && d.progress(a, c) >= d.target);
}

/** Records and pays out every newly met achievement; returns them. */
export function unlockAchievements(p: Progression, now = Date.now()): AchievementDef[] {
  const got = pendingUnlocks(p.account, p.char);
  if (!got.length) return got;
  const rankBefore = legacyRank(p.account.legacyXp);
  for (const d of got) {
    p.account.achievements[d.id] = now;
    p.account.legacyXp += ACHIEVEMENT_LEGACY_XP;
    const r = d.reward;
    p.account.wallet.silver += r.silver ?? 0;
    p.account.wallet.gold += r.gold ?? 0;
    for (const it of r.items ?? []) p.giveItem(it.id, it.count);
    if (r.title) addTitle(p.char, r.title);
  }
  p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  const rankAfter = legacyRank(p.account.legacyXp);
  if (rankAfter > rankBefore) {
    p.emit('legacyRank', { rank: rankAfter });
    p.emit('statsChanged', {});
  }
  return got;
}

/** Titles the roster unlocked through achievements. */
export function achievementTitles(a: AccountSave): string[] {
  return ACHIEVEMENTS.filter((d) => a.achievements[d.id] && d.reward.title).map(
    (d) => d.reward.title as string,
  );
}
